import { type CanvasTokenImageNode, type CanvasTokenNode } from '../store/slices/createCanvasTokenNodeSlice';
import { getBrandKitMentions } from '../store/useBrandKitMentionStore';
import { resolveGroupedTokenDisplay } from '../store/utils/resolveGroupedTokenDisplay';
import { type MentionItem, type MentionItemType } from '../ui/components/ChatComposer/mentions/types';
import { HEX_TOKEN_PREFIX, hexColorTokenAttrsFromRaw } from '../utils/hexColorInMessage';
import { getNodeLabel, getNodeThumbnailUrl } from './mediaFormatUtils';

const GROUP_THUMBNAIL_SIZE = 24;

export const MEDIA_PATTERN = /\[media:([^\]]+)\]/g;

export const REF_TOKEN_PATTERN = /\[ref:([a-z]+):([^\]]+)\]/g;

// 1=media, 2=mediaId | 3=ref, 4=refType, 5=refId
export const EMBEDDED_TOKEN_PATTERN = /\[(?:(media):([^\]]+)|(ref):([a-z]+):([^\]]+))\]/g;

export type ReferenceTokenType = Extract<MentionItemType, 'color' | 'font' | 'logo' | 'size' | 'set'>;

const REFERENCE_TOKEN_TYPES: ReadonlySet<string> = new Set(['color', 'font', 'logo', 'size', 'set']);

export const isReferenceTokenType = (type: string): type is ReferenceTokenType => REFERENCE_TOKEN_TYPES.has(type);

/**
 * Serialize a media reference to its embedded string format.
 */
export const serializeMediaReference = (id: string): string => `[media:${id}]`;

/** Ids embedded as `[media:id]` in a chat message, in document order. */
export const extractMediaReferenceIds = (message: string): string[] => {
  const ids: string[] = [];

  for (const match of message.matchAll(MEDIA_PATTERN)) {
    const id = match[1];

    if (id) {
      ids.push(id);
    }
  }

  return ids;
};

export const serializeReferenceToken = (tokenType: string, id: string): string => `[ref:${tokenType}:${id}]`;

export type ResolvedReferenceToken = {
  hex: string | null;
  id: string;
  name: string;
  thumbnailUrl: string | null;
  type: MentionItemType;
};

export type ResolvedMediaTokenAttrs = {
  id: string;
  name: string;
  thumbnailUrl: string | null;
  type: string;
};

export const resolveReferenceToken = (
  id: string,
  expectedType: string,
  brandKitMentions: MentionItem[] = getBrandKitMentions(),
): ResolvedReferenceToken | null => {
  const brandItem = brandKitMentions.find((item) => item.id === id);

  if (!brandItem || brandItem.type !== expectedType) {
    if (expectedType === 'color' && id.startsWith(HEX_TOKEN_PREFIX)) {
      return hexColorTokenAttrsFromRaw(id.slice(HEX_TOKEN_PREFIX.length));
    }

    return null;
  }

  return {
    hex: brandItem.hex ?? null,
    id: brandItem.id,
    name: brandItem.name,
    thumbnailUrl: brandItem.thumbnailUrl,
    type: brandItem.type,
  };
};

export const resolveMediaTokenAttrs = (
  nodes: Record<string, CanvasTokenNode>,
  id: string,
  thumbnailSize: number,
): ResolvedMediaTokenAttrs | null => {
  const resolved = resolveGroupedTokenDisplay(nodes, id, thumbnailSize);

  if (resolved?.type === 'media') {
    return {
      id,
      name: getNodeLabel(resolved.node),
      thumbnailUrl: getNodeThumbnailUrl(resolved.node, thumbnailSize),
      type: resolved.node.type,
    };
  }

  if (resolved?.type === 'group') {
    return {
      id: resolved.groupId,
      name: resolved.name,
      thumbnailUrl: resolved.thumbnailUrl,
      type: 'group',
    };
  }

  return null;
};

export const expandBrandKitRefsForAgent = (
  message: string,
  mentions: MentionItem[] = getBrandKitMentions(),
): string => {
  return message.replace(REF_TOKEN_PATTERN, (full, type, id) => {
    const item = mentions.find((mention) => mention.id === id);

    if (!item || item.type !== type) {
      if (type === 'color' && id.startsWith(HEX_TOKEN_PREFIX)) {
        return hexColorTokenAttrsFromRaw(id.slice(HEX_TOKEN_PREFIX.length))?.hex ?? full;
      }

      return full;
    }

    if (type === 'logo' && item.thumbnailUrl) {
      return serializeMediaReference(id);
    }

    if (type === 'color' && item.hex) {
      return `${item.name} (${item.hex})`;
    }

    if (type === 'font' && item.fontFamily) {
      return item.thumbnailUrl ? `${item.fontFamily} (${item.thumbnailUrl})` : item.fontFamily;
    }

    // The pixels are the point of a size reference, so spell them out rather than sending a name.
    if (type === 'size' && item.dimensionsLabel) {
      return item.dimensionsLabel;
    }

    // Individual member dimensions, not just a count, so the agent can act on a set the same way
    // it would if each size had been mentioned on its own.
    if (type === 'set' && item.memberLabels?.length) {
      return item.memberLabels.join(', ');
    }

    // Resolvable but degraded — still give the model a label.
    return item.name;
  });
};

export const getBrandKitLogoNodesForMessages = (
  messages: string[],
  mentions: MentionItem[] = getBrandKitMentions(),
): Record<string, CanvasTokenImageNode> => {
  const nodes: Record<string, CanvasTokenImageNode> = {};

  for (const message of messages) {
    for (const match of message.matchAll(REF_TOKEN_PATTERN)) {
      const type = match[1];
      const id = match[2];

      if (type !== 'logo') {
        continue;
      }

      const item = mentions.find((mention) => mention.id === id && mention.type === 'logo');

      if (!item?.thumbnailUrl) {
        continue;
      }

      const url = item.thumbnailUrl;
      nodes[id] = {
        fileName: item.name,
        group: { id },
        media: { image: url, original: url },
        type: 'image',
      };
    }
  }

  return nodes;
};

/**
 * Represents a parsed segment of a message string.
 */
export type MessageSegment =
  | { type: 'text'; value: string }
  | { type: 'media'; id: string; node: CanvasTokenNode }
  | { type: 'group'; groupId: string; memberCount: number; name: string; thumbnailUrl: string | null }
  | {
      hex: string | null;
      id: string;
      name: string;
      thumbnailUrl: string | null;
      tokenType: MentionItemType;
      type: 'reference';
    }
  | { type: 'unavailable' };

/**
 * Parse a message string into segments of text, media references, and unavailable markers.
 *
 * - `[media:id]` patterns are looked up in the provided nodes map.
 *   Found → media segment, not found → unavailable segment.
 * - `[ref:type:id]` patterns resolve against brand kit items in the mention store.
 *   Unresolvable refs stay as plain text (same as composer paste).
 * - Legacy `{{token:...}}` patterns are replaced with unavailable segments.
 * - Loose UUIDs in text that match a node ID are converted to media segments.
 * - Everything else is plain text.
 */
export const parseMessageSegments = (
  message: string,
  nodes: Record<string, CanvasTokenNode>,
  brandKitMentions: MentionItem[] = getBrandKitMentions(),
): MessageSegment[] => {
  // Replace legacy token patterns with a placeholder so we can handle them uniformly.
  const UNAVAILABLE_PLACEHOLDER = '\0UNAVAILABLE\0';
  const legacyTokenPattern = /\{\{token:\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}\}\}/g;
  const withLegacyReplaced = message.replace(legacyTokenPattern, UNAVAILABLE_PLACEHOLDER);

  const segments: MessageSegment[] = [];
  let lastIndex = 0;

  for (const match of withLegacyReplaced.matchAll(EMBEDDED_TOKEN_PATTERN)) {
    const matchStart = match.index;

    if (matchStart > lastIndex) {
      pushTextWithUnavailable(segments, withLegacyReplaced.slice(lastIndex, matchStart), UNAVAILABLE_PLACEHOLDER);
    }

    const mediaId = match[2];
    const refType = match[4];
    const refId = match[5];

    if (refId && refType) {
      const resolved = resolveReferenceToken(refId, refType, brandKitMentions);

      if (!resolved) {
        // Keep the raw token so history matches composer paste behavior when mentions are missing.
        segments.push({ type: 'text', value: match[0] });
      } else {
        segments.push({
          hex: resolved.hex,
          id: refId,
          name: resolved.name,
          thumbnailUrl: resolved.thumbnailUrl,
          tokenType: resolved.type,
          type: 'reference',
        });
      }
    } else if (mediaId) {
      const resolved = resolveGroupedTokenDisplay(nodes, mediaId, GROUP_THUMBNAIL_SIZE);

      if (resolved?.type === 'media') {
        segments.push({ id: mediaId, node: resolved.node, type: 'media' });
      } else if (resolved?.type === 'group') {
        segments.push({
          groupId: resolved.groupId,
          memberCount: resolved.memberCount,
          name: resolved.name,
          thumbnailUrl: resolved.thumbnailUrl,
          type: 'group',
        });
      } else {
        segments.push({ type: 'unavailable' });
      }
    }

    lastIndex = matchStart + match[0].length;
  }

  if (lastIndex < withLegacyReplaced.length) {
    pushTextWithUnavailable(segments, withLegacyReplaced.slice(lastIndex), UNAVAILABLE_PLACEHOLDER);
  }

  // Second pass: scan text segments for loose UUIDs that match known node IDs.
  // This catches bare UUIDs in messages that weren't wrapped in [media:...].
  if (Object.keys(nodes).length > 0) {
    return resolveLooseUuids(segments, nodes);
  }

  return segments;
};

/** Splits a text chunk on unavailable placeholders, pushing text and unavailable segments. */
const pushTextWithUnavailable = (segments: MessageSegment[], text: string, placeholder: string): void => {
  const parts = text.split(placeholder);

  for (let i = 0; i < parts.length; i++) {
    if (parts[i]) {
      segments.push({ type: 'text', value: parts[i] });
    }

    if (i < parts.length - 1) {
      segments.push({ type: 'unavailable' });
    }
  }
};

/**
 * Scan text segments for standalone UUIDs and replace them with media or unavailable segments.
 * UUIDs already consumed by [media:...] are not in text segments, so no double-processing.
 * Matched UUIDs → media segment; unmatched → unavailable segment.
 */
const resolveLooseUuids = (segments: MessageSegment[], nodes: Record<string, CanvasTokenNode>): MessageSegment[] => {
  // Capturing group approach avoids lookbehinds, which crash older mobile Safari (pre-iOS 16.4).
  // Group 1: leading whitespace (or empty at start of string). Group 2: the UUID.
  const standaloneUuidPattern = /(^|\s)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\s|$)/gi;
  const result: MessageSegment[] = [];

  for (const segment of segments) {
    if (segment.type !== 'text') {
      result.push(segment);
      continue;
    }

    let lastIndex = 0;
    let hasMatches = false;
    const text = segment.value;

    for (const match of text.matchAll(standaloneUuidPattern)) {
      hasMatches = true;
      const uuid = match[2].toLowerCase();
      const resolved = resolveGroupedTokenDisplay(nodes, uuid, GROUP_THUMBNAIL_SIZE);
      const matchStart = match.index + match[1].length;

      if (matchStart > lastIndex) {
        result.push({ type: 'text', value: text.slice(lastIndex, matchStart) });
      }

      if (resolved?.type === 'media') {
        result.push({ id: uuid, node: resolved.node, type: 'media' });
      } else if (resolved?.type === 'group') {
        result.push({
          groupId: resolved.groupId,
          memberCount: resolved.memberCount,
          name: resolved.name,
          thumbnailUrl: resolved.thumbnailUrl,
          type: 'group',
        });
      } else {
        result.push({ type: 'unavailable' });
      }

      lastIndex = matchStart + match[2].length;
    }

    if (!hasMatches) {
      result.push(segment);
    } else if (lastIndex < text.length) {
      result.push({ type: 'text', value: text.slice(lastIndex) });
    }
  }

  return result;
};
